// Sidebar — Cut AL rebuild.
//
// Intent: UAE SMB owner needs to navigate a fleet of 20+ role-specialized
// agents + 6 operational surfaces without scrolling through 25 flat items.
// 7 collapsible groups, each holds 2-5 destinations. Default open: Dashboard
// + Agents (the two the owner uses every visit). Open-state persists in
// localStorage so the owner's working layout survives reload.
//
// Per interface-design skill: same canvas color as main content, just a 1px
// border separation. No "sidebar world vs content world" split.
// Per huashu-design: no emoji icons. Inline SVG paths only. Single accent.

import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import ThemePicker from '@/components/common/ThemePicker';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { AGENT_META, agentLabel } from '@/components/ui/Icons';
import { buildSimpleNav } from './navModel';

type NavRole = 'owner' | 'admin' | 'worker';

interface NavItem {
  to: string;
  label: string;
  roles?: NavRole[];
  /** Per-tenant feature flag key — item hidden when the flag is explicitly
   * false on the tenant (see TenantContext.hasFeature). */
  feature?: string;
}

interface NavGroup {
  id: string;
  label: string;
  /** Inline SVG path d-attribute. Single icon per group, used in collapsed mode. */
  icon: string;
  defaultOpen?: boolean;
  items: NavItem[];
}

const ALL: NavRole[] = ['owner', 'admin', 'worker'];
const STAFF: NavRole[] = ['owner', 'admin'];
const OWNER: NavRole[] = ['owner'];

// 4 groups (down from 6). Calendar + Recurring fold into Orders as tabs;
// Documents/Memory/Vault fold into Knowledge as tabs; Mission Control retired;
// Policies + Services & Prices moved into Setup (configure-once, not daily).
// Only Overview + Agents open by default, so a fresh load shows ~4 items.
const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: 'M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10',
    defaultOpen: true,
    items: [
      { to: '/', label: 'Home', roles: ALL },
      { to: '/insights', label: 'Insights', roles: STAFF },
    ],
  },
  {
    id: 'agents',
    label: 'Agents',
    icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    defaultOpen: true,
    // Items are built per-tenant in the component: one entry per active agent
    // kind, then Test Chat, then the operator-only Manage agents surface.
    items: [],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    items: [
      { to: '/conversations', label: 'Conversations', roles: ALL },
      { to: '/orders', label: 'Orders', roles: ALL },
      { to: '/business-ops', label: 'Business Ops', roles: STAFF },
    ],
  },
  {
    id: 'setup',
    label: 'Setup',
    icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    items: [
      { to: '/knowledge', label: 'Knowledge', roles: STAFF },
      { to: '/catalog', label: 'Services & Prices', roles: STAFF },
      { to: '/growth', label: 'Growth Coach', roles: STAFF, feature: 'growth' },
      { to: '/connections', label: 'Connections', roles: OWNER },
      { to: '/team', label: 'Team', roles: STAFF },
      { to: '/settings', label: 'Settings', roles: STAFF },
    ],
  },
];

// v2 — group ids changed in the console consolidation (dashboard→overview,
// knowledge/growth folded away). Bumping the key resets stale saved sets so
// returning users get the new defaults instead of a half-collapsed nav.
const STORAGE_KEY = 'raftel_sidebar_open_groups_v2';

function loadOpenState(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set(NAV_GROUPS.filter((g) => g.defaultOpen).map((g) => g.id));
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set(NAV_GROUPS.filter((g) => g.defaultOpen).map((g) => g.id));
  }
}

// Rendered only for the platform admin — cross-tenant management surface.
const PLATFORM_GROUP: NavGroup = {
  id: 'platform',
  label: 'Platform',
  icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  items: [{ to: '/admin', label: 'Tenants', roles: ['owner', 'admin', 'worker'] }],
};

function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(loadOpenState);
  const { user } = useAuth();
  const { hasFeature, isSimpleMode, activeAgentKinds } = useTenant();
  const role = (user?.role === 'operator' ? 'owner' : (user?.role ?? 'worker')) as NavRole;

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(openGroups))); } catch { /* fail silent */ }
  }, [openGroups]);

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Operator Agents group — one entry per active agent kind (same source the
  // simple-mode nav uses), then Test Chat, then the operator-only manage
  // surface. With zero active agents the group still offers Test Chat +
  // Manage agents.
  const agentItems: NavItem[] = [
    ...activeAgentKinds
      .filter((k) => AGENT_META[k])
      .map((k) => ({ to: `/agents/${k}`, label: agentLabel(k), roles: ALL })),
    { to: '/test-chat', label: 'Test Chat', roles: ALL },
    { to: '/roles', label: 'Manage agents', roles: OWNER },
  ];

  // Simple-mode subscribers get a nav built from their live agents; operators
  // keep the full NAV_GROUPS (+ Platform group for platform admins). The
  // subscriber nav is structurally a NavGroup[] (items lack `roles`, which the
  // filter treats as "all roles").
  const baseGroups: NavGroup[] = isSimpleMode
    ? (buildSimpleNav(activeAgentKinds, hasFeature) as NavGroup[])
    : NAV_GROUPS.map((g) => (g.id === 'agents' ? { ...g, items: agentItems } : g));

  const visibleGroups = [...baseGroups, ...(user?.isPlatformAdmin && !isSimpleMode ? [PLATFORM_GROUP] : [])]
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) => (!i.roles || i.roles.includes(role)) && (!i.feature || hasFeature(i.feature)),
      ),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <aside
      className={clsx(
        // Same background as canvas — relies on a single border for separation,
        // not a different color (interface-design "sidebars same as canvas").
        'hidden md:flex flex-col bg-bg border-r border-line h-screen sticky top-0 transition-all duration-300 z-30',
        collapsed ? 'w-14' : 'w-56',
      )}
    >
      {/* Wordmark — Sand & Inkwell. Serif display, restrained capital R sigil.
          No glow, no gradient. Squint test: still reads "Raftel". */}
      <div className="flex items-center gap-2.5 px-4 py-5 border-b border-line">
        <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 border border-hairline-strong">
          <span className="font-display text-ink text-base leading-none" style={{ fontWeight: 600 }}>R</span>
        </div>
        {!collapsed && (
          <span className="font-display text-text" style={{ fontSize: '1.05rem', letterSpacing: '0.02em', fontWeight: 500 }}>
            Raftel
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-2 px-1.5">
        {visibleGroups.map((g) => {
          const isOpen = openGroups.has(g.id);
          return (
            <div key={g.id} className="mb-1">
              {!collapsed && (
                <button
                  onClick={() => toggleGroup(g.id)}
                  className="w-full flex items-center justify-between px-2.5 py-1.5 rounded text-[10px] font-semibold uppercase tracking-[0.12em] text-muted hover:text-text transition-colors"
                  aria-expanded={isOpen}
                >
                  <span>{g.label}</span>
                  <svg
                    width="10" height="10" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                    className={clsx('transition-transform', isOpen ? 'rotate-90' : '')}
                  >
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              {(isOpen || collapsed) && (
                <div className="space-y-px mt-0.5">
                  {g.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        clsx(
                          'flex items-center gap-2.5 px-2.5 py-1.5 rounded text-[13px] transition-colors',
                          isActive
                            ? 'bg-accent/12 text-text'
                            : 'text-muted hover:text-text hover:bg-panel-soft',
                          collapsed && 'justify-center px-2',
                        )
                      }
                    >
                      {/* Per interface-design: icons only when they clarify. Group has a single
                          icon shown when collapsed; expanded mode shows label only — no per-item
                          icon noise. */}
                      {collapsed ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                          <path d={g.icon} />
                        </svg>
                      ) : (
                        <span className="truncate">{item.label}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="p-2 border-t border-line space-y-2">
        {!collapsed && <ThemePicker compact />}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-center py-1.5 rounded text-muted hover:text-text hover:bg-panel-soft transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            className={clsx('transition-transform', collapsed && 'rotate-180')}
          >
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
